/*
 * Bundle substitution — client-side wiring.
 *
 * Reads the config emitted by sections/bundle-substitution.liquid and:
 *   • Sets the bundle product's hidden variant_id input to the auto-picked variant
 *   • Appends a "Substituted" line item property when a non-canonical variant was picked
 *   • On Add to Cart click, re-checks component stock via Shopify's Ajax API and
 *     re-picks if stock has changed since page load (closes the race window)
 *   • Watches for DOM mutations so dynamically-rendered forms (quick-add modals,
 *     ajax cart re-renders) also pick up the override — debounced to avoid churn
 */

(function () {
  // The section is rendered both in the product template AND from the layout
  // (`layout/theme.liquid`), so the JS gets loaded twice on bundle product
  // pages. Guard against double-init — without this, every observer/handler
  // would run twice.
  if (window.__bundleSubstitutionInitialized) return;
  window.__bundleSubstitutionInitialized = true;

  const configEl = document.querySelector("[data-bundle-substitution-config]");
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    console.error("[bundle-substitution] invalid config JSON", err);
    return;
  }

  if (!config.bestVariantId) return;

  const state = {
    pickedVariantId: config.bestVariantId,
    isSubstitution: !!config.isSubstitution,
    outOfStockTitles: Array.isArray(config.outOfStockTitles)
      ? config.outOfStockTitles.slice()
      : [],
  };

  document.body.classList.add("bundle-substitution-active");

  const PROPERTY_KEY = "Substituted";

  function buildPropertyValue() {
    if (!state.isSubstitution) return null;
    const titles = state.outOfStockTitles.filter(Boolean);
    if (titles.length === 0) {
      return "Auto-selected available flavor (a component was out of stock)";
    }
    const joined =
      titles.length === 1
        ? titles[0]
        : titles.slice(0, -1).join(", ") + " and " + titles[titles.length - 1];
    return joined + " out of stock — substituted with available flavor";
  }

  function applyToForm(form, idInput) {
    // The id input may live OUTSIDE the form, linked via the form="..." attribute
    // (standard Shopify pattern — see sections/product.liquid:235). So callers must
    // pass the input directly; we can't rely on form.querySelector here.
    if (idInput && String(idInput.value) !== String(state.pickedVariantId)) {
      idInput.value = state.pickedVariantId;
    }

    const propertyName = 'properties[' + PROPERTY_KEY + ']';
    // CRITICAL: use getAttribute('id'), NOT form.id — when the form has a child
    // input named "id", the named-element accessor on form returns the INPUT
    // element, not the form's HTML id string. Setting `form="[object HTMLInputElement]"`
    // breaks the form-attribute association and makes FormData skip the input.
    const formIdAttr = form.getAttribute("id") || "";

    // Look for existing property inputs associated with THIS form — either
    // descendants of the form OR external inputs linked via form="..." attribute.
    // We collect ALL of them so we can dedupe (previous bugs left orphans).
    const allWithName = document.querySelectorAll(
      'input[name="' + propertyName + '"]'
    );
    const matching = [];
    allWithName.forEach(function (el) {
      const linked = el.getAttribute("form");
      if (
        (linked && linked === formIdAttr) ||
        (!linked && el.closest("form") === form)
      ) {
        matching.push(el);
      }
    });

    if (state.isSubstitution) {
      const propValue = buildPropertyValue();
      if (!propValue) return;

      // Dedupe — keep one, drop the rest.
      const survivor = matching.shift();
      matching.forEach(function (extra) {
        if (extra.parentNode) extra.parentNode.removeChild(extra);
      });

      let propInput = survivor;
      if (!propInput) {
        propInput = document.createElement("input");
        propInput.type = "hidden";
        propInput.name = propertyName;
        // Append as a direct child of the form element. Descendant relationship
        // is enough for FormData to include it — do NOT also set the `form`
        // attribute. (Setting it requires the form's correct HTML id string,
        // which `form.id` doesn't reliably return when the form has a child
        // input named "id".)
        form.appendChild(propInput);
      } else {
        // Existing input — make sure it's a form descendant and has no
        // potentially-broken `form` attribute.
        if (propInput.hasAttribute("form")) {
          propInput.removeAttribute("form");
        }
        if (propInput.parentNode !== form) {
          form.appendChild(propInput);
        }
      }
      if (propInput.value !== propValue) {
        propInput.value = propValue;
      }
    } else {
      // No substitution — remove all matching property inputs.
      matching.forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    }
  }

  // Forms relevant to our bundle. Identified once (when their variant id
  // input had an initial candidate value) and remembered, so subsequent
  // applyEverywhere() calls don't lose them once we've overridden the value.
  const BUNDLE_FORM_MARKER = "data-bundle-substitution-form";
  const candidateVariantIds = new Set(
    (config.candidates || []).map(function (c) {
      return String(c.variantId);
    })
  );

  function applyEverywhere() {
    if (candidateVariantIds.size === 0) return;

    const idInputs = document.querySelectorAll('input[name="id"]');
    idInputs.forEach(function (input) {
      let form = input.closest("form");
      if (!form && input.getAttribute("form")) {
        form = document.getElementById(input.getAttribute("form"));
      }
      if (!form) return;

      const action = form.getAttribute("action") || "";
      if (!action.includes("/cart/add")) return;

      // Identify bundle forms by their INITIAL variant id matching one of our
      // candidates. We can't filter by product-id (the main bundle form
      // doesn't have one — it's spread across sibling inputs in <product-form>),
      // but matching against config.candidates is precise. Once identified,
      // we mark the form so the check survives subsequent overrides.
      if (!form.hasAttribute(BUNDLE_FORM_MARKER)) {
        if (!candidateVariantIds.has(String(input.value))) return;
        form.setAttribute(BUNDLE_FORM_MARKER, "1");
      }

      applyToForm(form, input);
    });
  }

  // -------------------------------------------------------------------------
  // Click-time re-check via Shopify Ajax API.
  // -------------------------------------------------------------------------

  const variantAvailabilityCache = new Map();

  async function fetchVariantAvailability(productHandle, variantId) {
    const cacheKey = String(variantId);
    if (variantAvailabilityCache.has(cacheKey)) {
      return variantAvailabilityCache.get(cacheKey);
    }

    try {
      const res = await fetch("/products/" + productHandle + ".js");
      if (!res.ok) return null;
      const product = await res.json();
      const variant = product.variants.find(function (v) {
        return String(v.id) === cacheKey;
      });
      if (!variant) return null;

      const isAvailable =
        variant.inventory_management == null ||
        variant.inventory_management === "" ||
        (variant.inventory_quantity != null && variant.inventory_quantity > 0);

      variantAvailabilityCache.set(cacheKey, isAvailable);
      return isAvailable;
    } catch (err) {
      console.warn("[bundle-substitution] availability fetch failed", err);
      return null;
    }
  }

  async function recomputeBestCandidate() {
    if (!Array.isArray(config.candidates) || config.candidates.length === 0) {
      return null;
    }

    const needed = new Map();
    for (const cand of config.candidates) {
      for (const req of cand.requires || []) {
        if (!needed.has(String(req.variantId))) {
          needed.set(String(req.variantId), req);
        }
      }
    }

    const availabilities = {};
    await Promise.all(
      Array.from(needed.values()).map(async function (req) {
        const avail = await fetchVariantAvailability(
          req.productHandle,
          req.variantId
        );
        availabilities[String(req.variantId)] = avail;
      })
    );

    const sorted = config.candidates
      .slice()
      .sort(function (a, b) {
        return (a.priority || 0) - (b.priority || 0);
      });

    for (const cand of sorted) {
      let allAvail = true;
      for (const req of cand.requires || []) {
        if (availabilities[String(req.variantId)] === false) {
          allAvail = false;
          break;
        }
      }
      if (allAvail) {
        const defaultCand = config.candidates.find(function (c) {
          return c.priority === 0;
        });

        const oosTitles = [];
        if (defaultCand && cand !== defaultCand) {
          for (const req of defaultCand.requires || []) {
            if (availabilities[String(req.variantId)] === false) {
              oosTitles.push(req.title);
            }
          }
        }

        return {
          pickedVariantId: cand.variantId,
          isSubstitution: !defaultCand || cand !== defaultCand,
          outOfStockTitles: oosTitles,
        };
      }
    }

    return null;
  }

  // -------------------------------------------------------------------------
  // Click handling.
  //
  // We intercept the Add to Cart button click, run the stock re-check, then
  // re-fire the click so the theme's normal submit handler runs with the
  // updated variant id. The `letThroughOnce` flag prevents the synthetic click
  // from re-entering our handler (which would loop forever).
  // -------------------------------------------------------------------------

  const CLICK_ATTACHED_ATTR = "data-bundle-substitution-attached";
  const PASS_THROUGH_ATTR = "data-bundle-substitution-pass-through";

  function attachClickHandlers() {
    const buttons = document.querySelectorAll(
      'button[type="submit"][name="add"], button[type="submit"][data-add-to-cart], button[data-product-form-submit]'
    );
    buttons.forEach(function (btn) {
      if (btn.getAttribute(CLICK_ATTACHED_ATTR) === "1") return;
      btn.setAttribute(CLICK_ATTACHED_ATTR, "1");

      btn.addEventListener(
        "click",
        async function (e) {
          // Synthetic re-click after our recompute — let it pass through to
          // the theme's normal listeners, then clear the flag.
          if (btn.getAttribute(PASS_THROUGH_ATTR) === "1") {
            btn.removeAttribute(PASS_THROUGH_ATTR);
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          const fresh = await recomputeBestCandidate();
          if (fresh) {
            state.pickedVariantId = fresh.pickedVariantId;
            state.isSubstitution = fresh.isSubstitution;
            state.outOfStockTitles = fresh.outOfStockTitles;
            applyEverywhere();
          }

          // Mark the next click as a pass-through, then synthesize it.
          btn.setAttribute(PASS_THROUGH_ATTR, "1");
          btn.click();
        },
        true // capture phase, beats other listeners
      );
    });
  }

  // -------------------------------------------------------------------------
  // (Previous build had a MutationObserver here. Removed in favour of a
  // simple one-shot + delayed re-run pattern in init() — heavy storefronts
  // with active third-party widgets thrashed the observer callback.)
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // Customer-visible substitution notice — iPhone-message-style bubble from
  // Tom that slides in under the Add to Cart button. Typing-dots show first,
  // then the personal message fades in.
  // -------------------------------------------------------------------------

  const THREAD_CLASS = "bundle-msg-thread";
  const TYPING_DURATION_MS = 1100;

  // -------------------------------------------------------------------------
  // Random sender + random message variation per page load. Both founders
  // (Tom & Bas) have 4 variations each, written in the GONNA brand voice
  // (see anthropic-skills:gonna-voice).
  // -------------------------------------------------------------------------

  const SENDERS = [
    { name: "Tom", initial: "T" },
    { name: "Bas", initial: "B" },
  ];

  // Messages handle three swap shapes:
  //  • flavor only ("Dragonfruit" -> "Grapefruit")
  //  • bottle only ("the Sand bottle" -> "the Highlighter bottle")
  //  • both ("Dragonfruit and the Sand bottle" -> "Grapefruit and the Highlighter bottle")
  // Placeholders are kept mid-sentence so the lower-case "the " prefix for
  // bottles doesn't end up capitalised oddly.
  const MESSAGES = {
    Tom: [
      "Hey, Tom here. 👋 We ran out of {OOS_DESC} faster than we restocked. Apologies. Your bundle's got {SUB_DESC} in its place. Same carbs, same hit at kilometre 28. We'll ping you when stock's back.",
      "Tom here, real quick. 👋 We're out of {OOS_DESC}, sorry about that. We've swapped in {SUB_DESC}. Works just as well. Order ships as normal.",
      "Hey, Tom. 👋 Confession: {OOS_DESC} sold out on us. Not our finest moment 🙈. Your bundle goes out with {SUB_DESC} instead. Same fuel, same dose. I'll drop you a line when stock's back.",
      "Tom here. 👋 We blew through our stock of {OOS_DESC} faster than expected. Sorry about that. Your bundle ships with {SUB_DESC} instead. Same setup, same fuel.",
    ],
    Bas: [
      "Hey, Bas here. 👋 Heads up: {OOS_DESC} is out of stock for a couple of weeks. We've put {SUB_DESC} in your bundle instead. Same formula, same effect. Apologies for the swap.",
      "Bas here. 👋 Quick heads-up: {OOS_DESC} sold out before our next batch landed. Your bundle goes out with {SUB_DESC} instead. Sorry it isn't your first pick. We'll be in touch the second we restock.",
      "Hey, Bas here. 👋 Bit of bad timing on our side: {OOS_DESC} is empty 🙈. Your bundle ships with {SUB_DESC} in its place. Same setup, just a different mix. Sorry for the shuffle.",
      "Bas here. 👋 We ran out of {OOS_DESC} faster than we forecasted. That's on us. We're putting {SUB_DESC} in your bundle instead. We'll send a note as soon as stock's back.",
    ],
  };

  const pickedSender = SENDERS[Math.floor(Math.random() * SENDERS.length)];
  const pickedTemplate = (function () {
    const pool = MESSAGES[pickedSender.name];
    return pool[Math.floor(Math.random() * pool.length)];
  })();
  const PERSONALIZED_MESSAGE = pickedTemplate
    .replace(/\{OOS_DESC\}/g, config.oosDescription || "one of the items")
    .replace(/\{SUB_DESC\}/g, config.subDescription || "an available substitute");

  function personalizedMessage() {
    return PERSONALIZED_MESSAGE;
  }

  function buildNoticeElement() {
    const wrapper = document.createElement("div");
    wrapper.className = THREAD_CLASS;
    wrapper.setAttribute("role", "status");
    wrapper.setAttribute("aria-live", "polite");

    const sender = document.createElement("div");
    sender.className = "bundle-msg-sender";

    const avatar = document.createElement("span");
    avatar.className = "bundle-msg-avatar";
    avatar.setAttribute("aria-hidden", "true");
    avatar.textContent = pickedSender.initial;

    const nameEl = document.createElement("span");
    nameEl.className = "bundle-msg-name";
    nameEl.textContent = pickedSender.name;

    sender.appendChild(avatar);
    sender.appendChild(nameEl);

    const bubble = document.createElement("div");
    bubble.className = "bundle-msg-bubble";

    const typing = document.createElement("span");
    typing.className = "bundle-msg-typing";
    typing.setAttribute("aria-hidden", "true");
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement("span");
      dot.className = "bundle-msg-dot";
      typing.appendChild(dot);
    }
    bubble.appendChild(typing);

    wrapper.appendChild(sender);
    wrapper.appendChild(bubble);

    // After the typing animation, swap dots for the actual (personalized) message.
    setTimeout(function () {
      if (!wrapper.isConnected) return;
      const t = bubble.querySelector(".bundle-msg-typing");
      if (t && t.parentNode) t.parentNode.removeChild(t);
      const text = document.createElement("span");
      text.className = "bundle-msg-text";
      text.textContent = personalizedMessage();
      bubble.appendChild(text);
    }, TYPING_DURATION_MS);

    return wrapper;
  }

  function findBundleAddToCartButton() {
    // The rebrand theme's main product form has id like
    //   ProductForm--template--XXX__main-<productId>
    // We require both:
    //   - the id starts with `ProductForm--` (capital P; rules out lowercase
    //     siblings like `product-form-installment-...` that also live in the
    //     same section but have no Add to Cart button)
    //   - the id contains `__main` (rules out upsell/recommendation forms
    //     such as `ProductForm--api-upsell-product-...`)
    const bundleForms = Array.from(
      document.querySelectorAll("form[" + BUNDLE_FORM_MARKER + "]")
    );
    const mainForm = bundleForms.find(function (f) {
      const id = f.getAttribute("id") || "";
      if (id.indexOf("ProductForm--") !== 0) return false;
      if (id.indexOf("__main") === -1) return false;
      return !!(
        f.querySelector('button[name="add"]') ||
        f.querySelector('button[type="submit"]')
      );
    });
    if (mainForm) {
      return (
        mainForm.querySelector('button[name="add"]') ||
        mainForm.querySelector('button[type="submit"]')
      );
    }
    return null;
  }

  function syncNotice() {
    const existing = document.querySelector("." + THREAD_CLASS);
    const shouldShow = state.isSubstitution && PERSONALIZED_MESSAGE;

    if (!shouldShow) {
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
      }
      return;
    }

    if (existing) {
      // Already injected. Update the text only if it has changed; don't
      // re-trigger the typing animation.
      const textEl = existing.querySelector(".bundle-msg-text");
      if (textEl && textEl.textContent !== PERSONALIZED_MESSAGE) {
        textEl.textContent = PERSONALIZED_MESSAGE;
      }
      return;
    }

    const button = findBundleAddToCartButton();
    if (!button || !button.parentNode) return;

    const thread = buildNoticeElement();
    button.insertAdjacentElement("afterend", thread);
  }

  function runAll() {
    applyEverywhere();
    attachClickHandlers();
    syncNotice();
  }

  function init() {
    runAll();
    // One delayed re-run to catch forms that render asynchronously
    // (sticky add-to-cart, quick-add modals). Beyond that we trust the page.
    setTimeout(runAll, 1200);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
